import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter 
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { 
  Users, 
  Plus, 
  Search, 
  Filter, 
  Eye, 
  Pencil, 
  Trash2, 
  Link, 
  Unlink,
  CheckCircle,
  XCircle,
  Clock,
  Upload,
  UserPlus,
  Building,
  Mail,
  Phone,
  FileText,
  MoreHorizontal
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

export default function ProfessionalsPage() {
  const { api, user } = useAuth();
  const [professionals, setProfessionals] = useState([]);
  const [pendingRegistrations, setPendingRegistrations] = useState([]);
  const [professionalTypes, setProfessionalTypes] = useState([]);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  
  // Dialogs
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [selectedProfessional, setSelectedProfessional] = useState(null);
  
  const [formData, setFormData] = useState({
    professional_type: '',
    title: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    organisation: '',
    role_title: '',
    registration_number: '',
    registration_body: '',
    specialties: [],
    address: '',
    notes: ''
  });

  const [linkData, setLinkData] = useState({
    child_id: '',
    relationship_type: '',
    notes: ''
  });

  const canManage = ['director', 'admin', 'hr', 'clinical_lead', 'educational_lead'].includes(user?.role);
  const canApprove = ['director', 'admin', 'hr'].includes(user?.role);

  useEffect(() => {
    fetchData();
  }, [search, typeFilter]);

  const fetchData = async () => {
    try {
      const [profResponse, typesResponse, childrenResponse] = await Promise.all([
        api.get('/professionals', { params: { search, professional_type: typeFilter !== 'all' ? typeFilter : undefined } }),
        api.get('/professionals/types'),
        api.get('/patients')
      ]);
      
      setProfessionals(profResponse.data);
      setProfessionalTypes(typesResponse.data);
      setChildren(childrenResponse.data);
      
      if (canApprove) {
        const pendingResponse = await api.get('/professionals/registrations/pending');
        setPendingRegistrations(pendingResponse.data);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load professionals');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/professionals', formData);
      toast.success('Professional created successfully');
      setAddDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create professional');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/professionals/${selectedProfessional.id}`, formData);
      toast.success('Professional updated successfully');
      setViewDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update professional');
    }
  };

  const handleDeactivate = async (id) => {
    if (!confirm('Are you sure you want to deactivate this professional?')) return;
    try {
      await api.delete(`/professionals/${id}`);
      toast.success('Professional deactivated');
      fetchData();
    } catch (error) {
      toast.error('Failed to deactivate professional');
    }
  };

  const handleLinkChild = async () => {
    if (!linkData.child_id || !linkData.relationship_type) {
      toast.error('Please select a child and relationship type');
      return;
    }
    
    try {
      await api.post('/professionals/link-child', {
        professional_id: selectedProfessional.id,
        child_id: linkData.child_id,
        relationship_type: linkData.relationship_type,
        notes: linkData.notes
      });
      toast.success('Child linked successfully');
      setLinkDialogOpen(false);
      setLinkData({ child_id: '', relationship_type: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to link child');
    }
  };

  const handleUnlinkChild = async (professionalId, childId) => {
    if (!confirm('Are you sure you want to unlink this child?')) return;
    try {
      await api.delete(`/professionals/${professionalId}/unlink-child/${childId}`);
      toast.success('Child unlinked');
      fetchData();
    } catch (error) {
      toast.error('Failed to unlink child');
    }
  };

  const handleApproveRegistration = async (registrationId) => {
    try {
      await api.post(`/professionals/registrations/${registrationId}/approve`);
      toast.success('Registration approved');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve registration');
    }
  };

  const handleRejectRegistration = async (registrationId) => {
    const reason = prompt('Please provide a reason for rejection (optional):');
    try {
      await api.post(`/professionals/registrations/${registrationId}/reject`, null, {
        params: { reason }
      });
      toast.success('Registration rejected');
      fetchData();
    } catch (error) {
      toast.error('Failed to reject registration');
    }
  };

  const resetForm = () => {
    setFormData({
      professional_type: '',
      title: '',
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      organisation: '',
      role_title: '',
      registration_number: '',
      registration_body: '',
      specialties: [],
      address: '',
      notes: ''
    });
  };

  const openViewDialog = (professional) => {
    setSelectedProfessional(professional);
    setFormData({
      professional_type: professional.professional_type || '',
      title: professional.title || '',
      first_name: professional.first_name || '',
      last_name: professional.last_name || '',
      email: professional.email || '',
      phone: professional.phone || '',
      organisation: professional.organisation || '',
      role_title: professional.role_title || '',
      registration_number: professional.registration_number || '',
      registration_body: professional.registration_body || '',
      specialties: professional.specialties || [],
      address: professional.address || '',
      notes: professional.notes || ''
    });
    setViewDialogOpen(true);
  };

  const openLinkDialog = (professional) => {
    setSelectedProfessional(professional);
    setLinkDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-[#1e3a5f] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="professionals-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1e3a5f] flex items-center gap-2">
            <Users className="w-7 h-7" />
            External Professionals
          </h1>
          <p className="text-slate-500 mt-1">Manage GPs, consultants, social workers and other professionals</p>
        </div>
        
        {canManage && (
          <Button 
            onClick={() => { resetForm(); setAddDialogOpen(true); }}
            className="bg-[#1e3a5f] hover:bg-[#2e4a6f]"
            data-testid="add-professional-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Professional
          </Button>
        )}
      </div>

      {/* Pending Registrations Alert */}
      {canApprove && pendingRegistrations.length > 0 && (
        <Card className="border-yellow-300 bg-yellow-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2 text-yellow-800">
              <Clock className="w-5 h-5" />
              Pending Self-Registrations ({pendingRegistrations.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {pendingRegistrations.slice(0, 3).map((reg) => (
                <div key={reg.id} className="flex items-center justify-between bg-white p-3 rounded-lg border">
                  <div>
                    <p className="font-medium">{reg.first_name} {reg.last_name}</p>
                    <p className="text-sm text-slate-500">{reg.professional_type} • {reg.organisation || 'No organisation'}</p>
                    <p className="text-xs text-slate-400">"{reg.relationship_statement}"</p>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="text-green-600 border-green-600"
                      onClick={() => handleApproveRegistration(reg.id)}
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Approve
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="text-red-600 border-red-600"
                      onClick={() => handleRejectRegistration(reg.id)}
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      Reject
                    </Button>
                  </div>
                </div>
              ))}
              {pendingRegistrations.length > 3 && (
                <p className="text-sm text-slate-500 text-center">
                  +{pendingRegistrations.length - 3} more pending...
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search and Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
          <Input
            placeholder="Search by name, email, or organisation..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="search-input"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[200px]">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filter by type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {professionalTypes.map((type) => (
              <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Professionals Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {professionals.map((prof) => (
          <Card key={prof.id} className="hover:shadow-md transition-shadow" data-testid={`professional-card-${prof.id}`}>
            <CardContent className="pt-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#1e3a5f] rounded-full flex items-center justify-center text-white font-semibold">
                    {prof.first_name?.[0]}{prof.last_name?.[0]}
                  </div>
                  <div>
                    <h3 className="font-semibold">{prof.full_name}</h3>
                    <Badge variant="outline" className="mt-1">{prof.professional_type.replace('_', ' ')}</Badge>
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm">
                      <MoreHorizontal className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem onClick={() => openViewDialog(prof)}>
                      <Eye className="w-4 h-4 mr-2" />
                      View / Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => openLinkDialog(prof)}>
                      <Link className="w-4 h-4 mr-2" />
                      Link to Child
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {canManage && (
                      <DropdownMenuItem 
                        onClick={() => handleDeactivate(prof.id)}
                        className="text-red-600"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Deactivate
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              
              <div className="mt-4 space-y-2 text-sm text-slate-600">
                {prof.organisation && (
                  <p className="flex items-center gap-2">
                    <Building className="w-4 h-4 text-slate-400" />
                    {prof.organisation}
                  </p>
                )}
                {prof.email && (
                  <p className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-slate-400" />
                    {prof.email}
                  </p>
                )}
                {prof.phone && (
                  <p className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-slate-400" />
                    {prof.phone}
                  </p>
                )}
                {prof.registration_number && (
                  <p className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-slate-400" />
                    {prof.registration_body}: {prof.registration_number}
                  </p>
                )}
              </div>
              
              {prof.linked_children?.length > 0 && (
                <div className="mt-3 pt-3 border-t">
                  <p className="text-xs text-slate-500 mb-1">Linked to {prof.linked_children.length} child(ren)</p>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {professionals.length === 0 && (
        <Card className="py-12">
          <CardContent className="text-center">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-600">No professionals found</h3>
            <p className="text-slate-400 mt-1">
              {search || typeFilter !== 'all' ? 'Try adjusting your search or filters' : 'Add your first external professional'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Add Professional Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Add New Professional
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Professional Type *</Label>
                <Select 
                  value={formData.professional_type} 
                  onValueChange={(v) => setFormData({...formData, professional_type: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type..." />
                  </SelectTrigger>
                  <SelectContent>
                    {professionalTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Title</Label>
                <Select 
                  value={formData.title} 
                  onValueChange={(v) => setFormData({...formData, title: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Dr">Dr</SelectItem>
                    <SelectItem value="Mr">Mr</SelectItem>
                    <SelectItem value="Mrs">Mrs</SelectItem>
                    <SelectItem value="Ms">Ms</SelectItem>
                    <SelectItem value="Miss">Miss</SelectItem>
                    <SelectItem value="Prof">Prof</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>First Name *</Label>
                <Input 
                  value={formData.first_name} 
                  onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Last Name *</Label>
                <Input 
                  value={formData.last_name} 
                  onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                  required
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input 
                  type="email"
                  value={formData.email} 
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input 
                  value={formData.phone} 
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Organisation / Practice</Label>
                <Input 
                  value={formData.organisation} 
                  onChange={(e) => setFormData({...formData, organisation: e.target.value})}
                  placeholder="e.g., NHS Trust, Private Clinic"
                />
              </div>
              <div className="space-y-2">
                <Label>Job Title</Label>
                <Input 
                  value={formData.role_title} 
                  onChange={(e) => setFormData({...formData, role_title: e.target.value})}
                  placeholder="e.g., Consultant Paediatrician"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Registration Number</Label>
                <Input 
                  value={formData.registration_number} 
                  onChange={(e) => setFormData({...formData, registration_number: e.target.value})}
                  placeholder="e.g., GMC number"
                />
              </div>
              <div className="space-y-2">
                <Label>Registration Body</Label>
                <Select 
                  value={formData.registration_body} 
                  onValueChange={(v) => setFormData({...formData, registration_body: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GMC">GMC (General Medical Council)</SelectItem>
                    <SelectItem value="HCPC">HCPC (Health & Care Professions)</SelectItem>
                    <SelectItem value="NMC">NMC (Nursing & Midwifery)</SelectItem>
                    <SelectItem value="SWE">SWE (Social Work England)</SelectItem>
                    <SelectItem value="GPhC">GPhC (General Pharmaceutical)</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Address</Label>
              <Textarea 
                value={formData.address} 
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                rows={2}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea 
                value={formData.notes} 
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                rows={2}
                placeholder="Any additional information..."
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAddDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#1e3a5f]">Create Professional</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View/Edit Professional Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5" />
              Edit Professional
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4">
            {/* Same form fields as Add Dialog */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Professional Type *</Label>
                <Select 
                  value={formData.professional_type} 
                  onValueChange={(v) => setFormData({...formData, professional_type: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type..." />
                  </SelectTrigger>
                  <SelectContent>
                    {professionalTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Title</Label>
                <Select 
                  value={formData.title} 
                  onValueChange={(v) => setFormData({...formData, title: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Dr">Dr</SelectItem>
                    <SelectItem value="Mr">Mr</SelectItem>
                    <SelectItem value="Mrs">Mrs</SelectItem>
                    <SelectItem value="Ms">Ms</SelectItem>
                    <SelectItem value="Miss">Miss</SelectItem>
                    <SelectItem value="Prof">Prof</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>First Name *</Label>
                <Input 
                  value={formData.first_name} 
                  onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Last Name *</Label>
                <Input 
                  value={formData.last_name} 
                  onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                  required
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input 
                  type="email"
                  value={formData.email} 
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input 
                  value={formData.phone} 
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Organisation / Practice</Label>
                <Input 
                  value={formData.organisation} 
                  onChange={(e) => setFormData({...formData, organisation: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Job Title</Label>
                <Input 
                  value={formData.role_title} 
                  onChange={(e) => setFormData({...formData, role_title: e.target.value})}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Registration Number</Label>
                <Input 
                  value={formData.registration_number} 
                  onChange={(e) => setFormData({...formData, registration_number: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Registration Body</Label>
                <Select 
                  value={formData.registration_body} 
                  onValueChange={(v) => setFormData({...formData, registration_body: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GMC">GMC</SelectItem>
                    <SelectItem value="HCPC">HCPC</SelectItem>
                    <SelectItem value="NMC">NMC</SelectItem>
                    <SelectItem value="SWE">SWE</SelectItem>
                    <SelectItem value="GPhC">GPhC</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Address</Label>
              <Textarea 
                value={formData.address} 
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                rows={2}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea 
                value={formData.notes} 
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                rows={2}
              />
            </div>
            
            {/* Linked Children */}
            {selectedProfessional?.linked_children?.length > 0 && (
              <div className="space-y-2 border-t pt-4">
                <Label>Linked Children</Label>
                <div className="space-y-1">
                  {selectedProfessional.linked_children.map((childId) => {
                    const child = children.find(c => c.id === childId);
                    return child ? (
                      <div key={childId} className="flex items-center justify-between bg-slate-50 p-2 rounded">
                        <span className="text-sm">{child.first_name} {child.last_name}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleUnlinkChild(selectedProfessional.id, childId)}
                          className="text-red-500"
                        >
                          <Unlink className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : null;
                  })}
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setViewDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#1e3a5f]">Save Changes</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Link Child Dialog */}
      <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link className="w-5 h-5" />
              Link Child to {selectedProfessional?.full_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Select Child *</Label>
              <Select 
                value={linkData.child_id} 
                onValueChange={(v) => setLinkData({...linkData, child_id: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a child..." />
                </SelectTrigger>
                <SelectContent>
                  {children.filter(c => !selectedProfessional?.linked_children?.includes(c.id)).map((child) => (
                    <SelectItem key={child.id} value={child.id}>
                      {child.first_name} {child.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Relationship Type *</Label>
              <Select 
                value={linkData.relationship_type} 
                onValueChange={(v) => setLinkData({...linkData, relationship_type: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select relationship..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="attending_gp">Attending GP</SelectItem>
                  <SelectItem value="consultant">Consultant</SelectItem>
                  <SelectItem value="paediatrician">Paediatrician</SelectItem>
                  <SelectItem value="social_worker">Social Worker</SelectItem>
                  <SelectItem value="educational_psychologist">Educational Psychologist</SelectItem>
                  <SelectItem value="senco">SENCO</SelectItem>
                  <SelectItem value="health_visitor">Health Visitor</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea 
                value={linkData.notes} 
                onChange={(e) => setLinkData({...linkData, notes: e.target.value})}
                rows={2}
                placeholder="Any additional notes about this relationship..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleLinkChild} className="bg-[#1e3a5f]">Link Child</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
